import React from 'react';
import { Ticket, CreditCard, FileCheck, Users, TrendingUp, ArrowUpRight, ArrowDownRight, MoreHorizontal, Calendar, MapPin } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function DashboardPage() {
  const {
    travelRequests,
    confirmedTrips
  } = useAppContext();
  const pendingCount = travelRequests.filter(r => r.status === 'pending').length;
  // Combine context requests with hardcoded recent bookings for display
  const recentBookings = [...travelRequests.map(req => ({
    id: req.id,
    user: req.requester,
    destination: req.destination,
    date: req.submittedAt,
    amount: `$${req.amount}`,
    status: req.status === 'approved' ? 'Confirmed' : req.status === 'pending' ? 'Pending' : 'Rejected'
  })), {
    id: 'BK-7829',
    user: 'Sarah Connor',
    destination: 'London (LHR)',
    date: 'Oct 24, 2023',
    amount: '$1,240',
    status: 'Confirmed'
  }, {
    id: 'BK-7830',
    user: 'John Smith',
    destination: 'New York (JFK)',
    date: 'Oct 25, 2023',
    amount: '$850',
    status: 'Pending'
  }].slice(0, 5);
  const metrics = [{
    label: 'Tickets Issued',
    value: '1,248',
    change: '+12%',
    trend: 'up',
    icon: Ticket,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    trendColor: 'text-emerald-600'
  }, {
    label: 'Total Spend',
    value: '$482,500',
    change: '+8.2%',
    trend: 'up',
    icon: CreditCard,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    trendColor: 'text-emerald-600'
  }, {
    label: 'Pending Approvals',
    value: pendingCount.toString(),
    change: '-5%',
    trend: 'down',
    icon: FileCheck,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    trendColor: 'text-emerald-600'
  }, {
    label: 'Active Travelers',
    value: '142',
    change: '+18%',
    trend: 'up',
    icon: Users,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    trendColor: 'text-emerald-600'
  }];
  const upcomingTrips = [...confirmedTrips.map(trip => ({
    id: trip.id,
    title: trip.title,
    location: trip.location,
    date: trip.dates,
    travelers: 1
  })), {
    id: 101,
    title: 'Tech Conference',
    location: 'Las Vegas, NV',
    date: 'Dec 02-05',
    travelers: 12
  }].slice(0, 3);
  const departmentBudgets = [{
    name: 'Engineering',
    spent: 120000,
    total: 150000,
    percent: 80,
    color: 'bg-amber-500'
  }, {
    name: 'Sales',
    spent: 184000,
    total: 200000,
    percent: 92,
    color: 'bg-red-500'
  }, {
    name: 'Marketing',
    spent: 45000,
    total: 100000,
    percent: 45,
    color: 'bg-blue-500'
  }, {
    name: 'Operations',
    spent: 61000,
    total: 80000,
    percent: 76,
    color: 'bg-amber-500'
  }, {
    name: 'HR',
    spent: 18000,
    total: 50000,
    percent: 36,
    color: 'bg-blue-500'
  }];
  return <div className="space-y-6" data-id="element-645">
      <div className="flex items-center justify-between" data-id="element-646">
        <div data-id="element-647">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-648">
            Dashboard Overview
          </h2>
          <p className="text-slate-500" data-id="element-649">
            Welcome back, here's what's happening today.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-650">
          <button className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-651">
            Download Report
          </button>
          <button className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700" data-id="element-652">
            + New Booking
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-653">
        {metrics.map(metric => <div key={metric.label} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-654">
            <div className="flex items-center justify-between" data-id="element-655">
              <div className={`rounded-full p-3 ${metric.iconBg} ${metric.iconColor}`} data-id="element-656">
                <metric.icon className="h-6 w-6" data-id="element-657" />
              </div>
              <div className={`flex items-center gap-1 text-sm font-medium ${metric.trendColor}`} data-id="element-658">
                {metric.trend === 'up' ? <ArrowUpRight className="h-4 w-4" data-id="element-659" /> : <ArrowDownRight className="h-4 w-4" data-id="element-660" />}
                {metric.change}
              </div>
            </div>
            <div className="mt-4" data-id="element-661">
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-662">
                {metric.value}
              </h3>
              <p className="text-sm text-slate-500" data-id="element-663">{metric.label}</p>
            </div>
          </div>)}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-664">
        {/* Recent Bookings */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm lg:col-span-2" data-id="element-665">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4" data-id="element-666">
            <h3 className="font-semibold text-slate-800" data-id="element-667">Recent Bookings</h3>
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-668">
              View All
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-669">
            <table className="w-full text-left text-sm" data-id="element-670">
              <thead className="bg-slate-50 text-slate-500" data-id="element-671">
                <tr data-id="element-672">
                  <th className="px-6 py-3 font-medium" data-id="element-673">Booking ID</th>
                  <th className="px-6 py-3 font-medium" data-id="element-674">Traveler</th>
                  <th className="px-6 py-3 font-medium" data-id="element-675">Destination</th>
                  <th className="px-6 py-3 font-medium" data-id="element-676">Date</th>
                  <th className="px-6 py-3 font-medium" data-id="element-677">Amount</th>
                  <th className="px-6 py-3 font-medium" data-id="element-678">Status</th>
                  <th className="px-6 py-3 font-medium" data-id="element-679"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200" data-id="element-680">
                {recentBookings.map(booking => <tr key={booking.id} className="hover:bg-slate-50" data-id="element-681">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-682">
                      {booking.id}
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-683">{booking.user}</td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-684">
                      {booking.destination}
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-685">{booking.date}</td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-686">
                      {booking.amount}
                    </td>
                    <td className="px-6 py-4" data-id="element-687">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${booking.status === 'Confirmed' ? 'bg-emerald-100 text-emerald-800' : booking.status === 'Pending' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-688">
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-689">
                      <button className="text-slate-400 hover:text-slate-600" data-id="element-690">
                        <MoreHorizontal className="h-5 w-5" data-id="element-691" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Upcoming Trips */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-692">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4" data-id="element-693">
            <h3 className="font-semibold text-slate-800" data-id="element-694">Upcoming Trips</h3>
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-695">
              Calendar
            </button>
          </div>
          <div className="p-6 space-y-6" data-id="element-696">
            {upcomingTrips.map(trip => <div key={trip.id} className="flex items-start gap-4" data-id="element-697">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600" data-id="element-698">
                  <Calendar className="h-6 w-6" data-id="element-699" />
                </div>
                <div className="flex-1" data-id="element-700">
                  <h4 className="font-medium text-slate-900" data-id="element-701">{trip.title}</h4>
                  <div className="mt-1 flex items-center text-sm text-slate-500" data-id="element-702">
                    <MapPin className="mr-1 h-3.5 w-3.5" data-id="element-703" />
                    {trip.location}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs" data-id="element-704">
                    <span className="font-medium text-slate-600" data-id="element-705">
                      {trip.date}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600" data-id="element-706">
                      {trip.travelers} Travelers
                    </span>
                  </div>
                </div>
              </div>)}

            <button className="w-full rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900" data-id="element-707">
              View All Upcoming Trips
            </button>
          </div>
        </div>
      </div>

      {/* Budget Visualization */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-708">
        <div className="mb-6 flex items-center justify-between" data-id="element-709">
          <h3 className="font-semibold text-slate-800" data-id="element-710">
            Budget Utilization by Department
          </h3>
          <select className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm text-slate-700 focus:border-blue-500 focus:outline-none" data-id="element-711">
            <option data-id="element-712">This Quarter</option>
            <option data-id="element-713">Last Quarter</option>
            <option data-id="element-714">Year to Date</option>
          </select>
        </div>

        <div className="space-y-5" data-id="element-715">
          {departmentBudgets.map(dept => <div key={dept.name} data-id="element-716">
              <div className="flex justify-between items-end mb-1" data-id="element-717">
                <span className="text-sm font-medium text-slate-700" data-id="element-718">
                  {dept.name}
                </span>
                <div className="text-right" data-id="element-719">
                  <span className="text-sm font-bold text-slate-900" data-id="element-720">
                    ${(dept.spent / 1000).toFixed(0)}k
                  </span>
                  <span className="text-xs text-slate-500 ml-1" data-id="element-721">
                    / ${(dept.total / 1000).toFixed(0)}k
                  </span>
                </div>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-722">
                <div className={`h-2.5 rounded-full ${dept.color}`} style={{
              width: `${dept.percent}%`
            }} data-id="element-723"></div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}